import { useQuery } from '@apollo/client'
import { useMemo } from 'react'

import { queries } from '../graphql'

/**
 * Hook to check if user is an advisor and get their accessible funds
 * @returns {object} - { isAdvisor, accessibleFunds, totals, loading, error }
 */
export function useAdvisorAccess() {
  const { data, loading, error, refetch } = useQuery(queries.funds.advisorAccessibleFunds, {
    fetchPolicy: 'cache-and-network',
  })
  const { data: meData } = useQuery(queries.user.me, { fetchPolicy: 'cache-and-network' })

  const accessibleFunds = useMemo(() => {
    const funds = data?.me?.advisorAccessibleFunds || []
    // Sort alphabetically by owner name (client name)
    return [...funds].sort((a, b) => (a.ownerName || '').localeCompare(b.ownerName || ''))
  }, [data?.me?.advisorAccessibleFunds])

  // User is an advisor only if they have an open advisor-type fund profile.
  // Closing the advisor portal sets fundProfile.closed=true, which should revoke access.
  const hasOpenAdvisorPortal = useMemo(() => {
    const profiles = meData?.me?.fundProfiles || []
    return profiles.some((p) => p.fundType === 'advisor' && !p.closed)
  }, [meData?.me?.fundProfiles])

  const isAdvisor = accessibleFunds.length > 0 && hasOpenAdvisorPortal

  // Calculate combined totals across all accessible funds
  const totals = useMemo(
    () =>
      accessibleFunds.reduce(
        (acc, fund) => ({
          totalBalance: acc.totalBalance + (fund.totalBalance || 0),
          givingWalletBalance: acc.givingWalletBalance + (fund.givingWalletBalance || 0),
          investmentWalletBalance: acc.investmentWalletBalance + (fund.investmentWalletBalance || 0),
          clientCount: acc.clientCount + 1,
        }),
        {
          totalBalance: 0,
          givingWalletBalance: 0,
          investmentWalletBalance: 0,
          clientCount: 0,
        }
      ),
    [accessibleFunds]
  )

  return {
    isAdvisor,
    accessibleFunds,
    totals,
    loading,
    error,
    refetch,
  }
}
